import { FaireOncall } from "@faire/web-api--source/faire/logging/FaireOncall";
import { FairePod } from "@faire/web-api--source/faire/logging/FairePod";
import { FairePodExtensions } from "@faire/web-api--source/faire/logging/FairePodExtensions";
import { ProductArea } from "@faire/web-api--source/faire/logging/ProductArea";
import { ProductAreaExtensions } from "@faire/web-api--source/faire/logging/ProductAreaExtensions";
import { RetailerProductAreaRouteMatcher } from "@faire/web/common/routes/RetailerProductAreaRouteMatcher";
import { usePathname } from "next/navigation";
import { useMemo } from "react";

export interface ProductAreaOnCallInfo {
  onCallTag: string | undefined;
}

/**
 * Hook to get on-call information from the current route.
 * This is useful for error reporting, analytics, and incident routing.
 */
export const useProductAreaOnCall = (): ProductAreaOnCallInfo => {
  const pathname = usePathname();

  return useMemo(() => {
    // Match the current route to a product area
    const routeMatch =
      RetailerProductAreaRouteMatcher.matchProductArea(pathname);

    if (!routeMatch) {
      return {
        onCallTag: undefined,
      };
    }

    const { productArea } = routeMatch;

    // Get the product area enum value
    const productAreaEnum = ProductArea[productArea];

    // Get pod from product area
    const pod = ProductAreaExtensions.getPod(productAreaEnum);
    const podEnum = pod ? FairePod[pod] : undefined;

    // Get on-call from pod
    const oncall: FaireOncall | undefined = podEnum
      ? (FairePodExtensions.getOncall(podEnum) as FaireOncall | undefined)
      : undefined;

    /*
     * Replacing FAIRE_ONCALL_ similar to how it is done in the backend here:
     * https://github.com/Faire/backend/blob/96bf08a992c462a5a7002f1898508d12150a23dd/core/core-logging/src/main/kotlin/com/faire/core/logging/FaireOncallTagProvider.kt#L9
     */
    const removedPrefix = oncall
      ? oncall.replace("FAIRE_ONCALL_", "")
      : undefined;

    /*
     * Replacing _ with - and lowercasing similar to how it is done in the backend here:
     * https://github.com/Faire/backend/blob/96bf08a992c462a5a7002f1898508d12150a23dd/core/core-logging/src/main/kotlin/com/faire/core/logging/MetadataTagProvider.kt#L30
     */
    const removedUnderscore = removedPrefix
      ? removedPrefix.replace("_", "-")
      : undefined;

    // On-call tag is used to tag the on-call rotation in Datadog.
    const onCallTag = removedUnderscore
      ? removedUnderscore.toLowerCase()
      : undefined;

    return {
      onCallTag,
    };
  }, [pathname]);
};
